import { useState } from "react";
import { useTranslation } from "react-i18next";

type Meetup = {
  title: string;
  subtitle: string;
  goal: string;
  build: string[];
  learn: string[];
};

type Props = {
  onSelect?: (meetup: Meetup) => void;
};

export default function AcademyMeetups({ onSelect }: Props) {
  const { t } = useTranslation();

 const meetupsRaw = t("academyMeetups.items", {
  returnObjects: true,
});

  const meetups: Meetup[] = Array.isArray(meetupsRaw)
    ? meetupsRaw
    : [];

  const [active, setActive] = useState<number | null>(null);

  return (
    <section className="max-w-[1450px] mx-auto py-6 sm:py-10">

      {/* HEADER */}
      <div className="text-center space-y-6 mb-10 sm:mb-14">

        <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-[0.25em]">
          ⚡ {t("academyMeetups.badge")}
        </span>

        <h2 className="text-4xl sm:text-4xl lg:text-[3rem] font-black uppercase tracking-tighter text-slate-900 leading-[0.9] break-words">
          {t("academyMeetups.title1")}{" "}
          <span className="text-blue-600 italic">
            {t("academyMeetups.title2")}
          </span>
        </h2>

        <p className="max-w-4xl mx-auto text-sm sm:text-lg lg:text-xl text-slate-400 italic leading-relaxed px-2">
          {t("academyMeetups.subtitle")}
        </p>

      </div>

      {/* GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">

        {meetups.map((meetup, index) => (
          <div
            key={index}
            className="
              p-6 sm:p-8 lg:p-10
              bg-white
              border border-slate-100
              rounded-[28px] sm:rounded-[40px]
              flex flex-col justify-between
              transition-all duration-500
              hover:-translate-y-2
              hover:border-blue-500
              hover:shadow-[0_40px_100px_-20px_rgba(59,130,246,0.15)]
            "
          >

            {/* TOP */}
            <div className="space-y-8">

              <div className="flex items-center justify-between">

                <div className="w-12 sm:w-14 h-12 sm:h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-inner text-lg sm:text-xl font-black">
                  0{index + 1}
                </div>

                <span className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-600">
                  Meetup
                </span>

              </div>

              <div className="space-y-4 text-left">

                <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-slate-900 leading-tight break-words">
                  {meetup.title}
                </h3>

                <p className="text-base sm:text-lg font-bold text-slate-700 leading-snug">
                  {meetup.subtitle}
                </p>

                <div className="p-5 rounded-3xl bg-slate-50 border border-slate-100">

                  <span className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-600">
                    {t("goal")}
                  </span>

                  <p className="mt-3 text-sm text-slate-500 leading-relaxed italic">
                    {meetup.goal}
                  </p>

                </div>

              </div>

              {/* BUILD */}
              <div className="space-y-4">

                <button
                  onClick={() =>
                    setActive(active === index ? null : index)
                  }
                  className="w-full flex items-center justify-between text-left"
                >
                  <h4 className="text-sm font-black uppercase tracking-[0.2em] text-slate-900">
                    {t("build")}
                  </h4>

                  <span className="text-slate-400 text-xl font-light">
                    {active === index ? "−" : "+"}
                  </span>
                </button>

                <div
                  className={`
                    overflow-hidden transition-all duration-500
                    ${active === index ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}
                  `}
                >
                  <ul className="space-y-3 pt-2">
                    {meetup.build.map((item, idx) => (
                      <li key={idx} className="text-sm text-slate-600 font-medium flex items-start gap-3 leading-relaxed">
                        <span className="w-1.5 h-1.5 mt-2 bg-blue-600 rounded-full min-w-[6px]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

              </div>

              {/* LEARN */}
              <div className="space-y-4">

                <h4 className="text-sm font-black uppercase tracking-[0.2em] text-slate-900">
                  {t("academyMeetups.learn")}
                </h4>

                <ul className="space-y-3">
                  {meetup.learn.map((item, idx) => (
                    <li key={idx} className="text-sm text-slate-600 font-medium flex items-start gap-3 leading-relaxed">
                      <span className="w-1.5 h-1.5 mt-2 bg-slate-900 rounded-full min-w-[6px]" />
                      {item}
                    </li>
                  ))}
                </ul>

              </div>

            </div>

            {/* BUTTON */}
            <button
              onClick={() => onSelect?.(meetup)}
              className="w-full py-5 mt-10 bg-slate-950 text-white font-black text-[10px] uppercase tracking-widest rounded-3xl shadow-lg hover:bg-blue-600 transition-all active:scale-95"
            >
              {t("academyMeetups.register")}
            </button>

          </div>
        ))}

      </div>

      {/* BOTTOM */}
      <div className="mt-6 sm:mt-8 p-6 sm:p-8 lg:p-10 rounded-[28px] sm:rounded-[40px] bg-white border border-slate-100 text-center">

        <h3 className="text-2xl sm:text-2xl lg:text-[2rem] font-black uppercase tracking-tighter text-slate-900 leading-[0.9] break-words">
          {t("academyMeetups.bottomTitle")}
        </h3>

        <p className="max-w-3xl mx-auto mt-5 text-sm sm:text-lg text-slate-500 italic leading-relaxed">
          {t("academyMeetups.bottomText")}
        </p>

      </div>

    </section>
  );
}